import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import avatar from "../../asset/new/images/avatar-1577909_1280.png";
import { useNavigate, useParams } from "react-router-dom";
import { GetUser } from "../../Redux/Slices/User";
import AdminLayout from "./Common/AdminLayout";
import { CardSkeleton } from "./Common/LoadingSkeleton";
import { CandidateStatus } from "../../Redux/Slices/JobPost";
import toast from "react-hot-toast";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import "../../asset/new/css/admin-modern.css";

const InfoSection = ({ title, icon, children }) => (
  <div className="admin-card admin-applicant-section">
    <div className="admin-card-body">
      <h3 className="admin-applicant-section-title">
        <i className={`fas ${icon}`}></i>
        {title}
      </h3>
      {children}
    </div>
  </div>
);

const InfoGrid = ({ items }) => (
  <div className="admin-info-grid">
    {items.map(({ label, value, fullWidth }, index) => (
      <div
        className="admin-info-item"
        key={`${label}-${index}`}
        style={fullWidth ? { gridColumn: "1 / -1" } : undefined}
      >
        <span className="admin-info-label">{label}</span>
        <span className="admin-info-value">{value || "N/A"}</span>
      </div>
    ))}
  </div>
);

const ApplicantProfile = () => {
  const { userData } = useSelector((state) => state?.User);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, jobid } = useParams();
  const role = localStorage.getItem("role");
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const resumeUrl = process.env.REACT_APP_AZURE_STORAGE_URL;
  const applicantsPath =
    role === "Employer"
      ? `/employer/job-applicants/${jobid}`
      : `/admin/job-applicants/${jobid}`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(GetUser(id));
      setLoading(false);
    };
    fetchData();
    document.title = "Applicant Profile | CAJobPortal";
  }, [id, dispatch]);

  const handleDownload = async () => {
    if (!userData?.resume) return;
    window.open(resumeUrl + userData.resume, "_blank");
    await handleApplicantStatus("Downloaded");
  };

  const handleApplicantStatus = async (status) => {
    setUpdating(true);
    try {
      await CandidateStatus({
        user_id: id,
        job_id: jobid,
        action: status,
      });
      toast.success(`Status updated to ${status}`);
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdate = async () => {
    if (!action) {
      toast.error("Please select a status");
      return;
    }
    await handleApplicantStatus(action);
  };

  if (role !== "Admin" && role !== "Employer") {
    return null;
  }

  const industryName =
    userData?.career?.industry?.map((value) => value.name).join(", ") ||
    "Not Available";
  const itName =
    userData?.career?.skills
      ?.filter((value) => value.type === "IT")
      .map((value) => value.name)
      .join(", ") || "Not Available";
  const fnName =
    userData?.career?.skills
      ?.filter((value) => value.type === "Functional")
      .map((value) => value.name)
      .join(", ") || "Not Available";
  const location =
    userData?.career?.location?.join(", ") || "Not Available";

  return (
    <AdminLayout
      title="Applicant Profile"
      subtitle="Review candidate details and update application status"
    >
      <button
        type="button"
        className="admin-applicant-back"
        onClick={() => navigate(applicantsPath)}
      >
        <FaArrowLeft size={12} /> Back to Applicants
      </button>

      {loading ? (
        <CardSkeleton count={3} />
      ) : !userData ? (
        <div className="admin-card">
          <div className="admin-card-body admin-empty-state">
            <h4>Applicant Not Found</h4>
            <p>The requested candidate profile could not be loaded.</p>
          </div>
        </div>
      ) : (
        <div className="admin-applicant-layout">
          <aside className="admin-applicant-sidebar">
            <div className="admin-card">
              <div className="admin-card-body">
                <img
                  alt={userData?.name || "Applicant"}
                  src={userData?.profile_img || avatar}
                  className="admin-applicant-avatar"
                />
                <h2 className="admin-applicant-name">{userData?.name}</h2>
                <p className="admin-applicant-meta">
                  {userData?.experience_level || "Experience not specified"}
                </p>

                <div className="admin-applicant-actions">
                  <select
                    className="admin-select"
                    onChange={(e) => setAction(e.target.value)}
                    value={action}
                  >
                    <option value="">Select status</option>
                    <option value="Viewed">Viewed</option>
                    <option value="Downloaded">Downloaded</option>
                    <option value="Shortlisted">Shortlisted</option>
                    <option value="Not Suitable">Not Suitable</option>
                  </select>
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    onClick={handleUpdate}
                    disabled={updating}
                  >
                    {updating ? "Updating..." : "Update Status"}
                  </button>
                  {userData?.resume ? (
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="admin-btn admin-btn-success"
                      disabled={updating}
                    >
                      <FaDownload size={12} /> Download CV
                    </button>
                  ) : (
                    <button type="button" className="admin-btn admin-btn-secondary" disabled>
                      No Resume Available
                    </button>
                  )}
                </div>
              </div>
            </div>
          </aside>

          <div>
            <InfoSection title="Personal Info" icon="fa-user">
              <InfoGrid
                items={[
                  { label: "Name", value: userData?.name },
                  { label: "Email", value: userData?.email },
                  { label: "Phone", value: userData?.mobile },
                  { label: "Experience", value: userData?.experience_level },
                ]}
              />
            </InfoSection>

            {userData?.experience?.length > 0 && (
              <InfoSection title="Experience Details" icon="fa-briefcase">
                {userData.experience.map((value, index) => (
                  <div className="admin-section-item" key={index}>
                    <InfoGrid
                      items={[
                        { label: "Designation", value: value?.designation },
                        { label: "Company", value: value?.company?.name },
                        {
                          label: "Duration",
                          value: `${value?.duration?.from || "N/A"} to ${value?.duration?.to || "Present"}`,
                        },
                        {
                          label: "Profile Description",
                          value: value?.profile_description,
                          fullWidth: true,
                        },
                      ]}
                    />
                  </div>
                ))}
              </InfoSection>
            )}

            {userData?.prof_qualification?.length > 0 && (
              <InfoSection title="Professional Qualification" icon="fa-graduation-cap">
                {userData.prof_qualification.map((value, index) => (
                  <div className="admin-section-item" key={index}>
                    <InfoGrid
                      items={[
                        { label: "Degree", value: value?.degree?.name },
                        {
                          label: "Term of Passing",
                          value: `${value?.term_of_passing?.year || "N/A"} ${value?.term_of_passing?.month || ""}`.trim(),
                        },
                        { label: "Grade Score", value: value?.marks },
                        { label: "No. of Attempts", value: value?.attempts },
                      ]}
                    />
                  </div>
                ))}
              </InfoSection>
            )}

            {userData?.academic?.length > 0 && (
              <InfoSection title="Academic Details" icon="fa-school">
                {userData.academic.map((value, index) => (
                  <div className="admin-section-item" key={index}>
                    <InfoGrid
                      items={[
                        { label: "Education", value: value?.graduation },
                        { label: "Institution", value: value?.college },
                        { label: "Grade Score", value: value?.marks },
                        { label: "Year of Passing", value: value?.year_of_passing },
                      ]}
                    />
                  </div>
                ))}
              </InfoSection>
            )}

            <InfoSection title="Additional Details" icon="fa-info-circle">
              <InfoGrid
                items={[
                  { label: "Date of Birth", value: userData?.dob },
                  { label: "Gender", value: userData?.gender },
                  { label: "Marital Status", value: userData?.marital_status },
                  { label: "Address", value: userData?.address?.address1 },
                  { label: "Pincode", value: userData?.address?.pincode },
                ]}
              />
            </InfoSection>

            {userData?.career && (
              <InfoSection title="Career Profile" icon="fa-chart-line">
                <InfoGrid
                  items={[
                    {
                      label: "Profile Headline",
                      value: userData.career.profile_headline,
                      fullWidth: true,
                    },
                    {
                      label: "Career Accomplishments",
                      value: userData.career.accomplishments,
                      fullWidth: true,
                    },
                    {
                      label: "Expected Salary",
                      value: `${userData.career.expected_salary?.lac || 0} Lacs ${userData.career.expected_salary?.thousand || 0}`,
                    },
                    { label: "Industry", value: industryName },
                    { label: "Functional Skills", value: fnName },
                    { label: "IT Skills", value: itName },
                    { label: "Preferred Location", value: location, fullWidth: true },
                  ]}
                />
              </InfoSection>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ApplicantProfile;
